import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import { reviewsIconCollectionManagerToken } from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { SvelteComponent } from "svelte";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ListingProductInfoQuery from "../queries/ListingProductInfoQuery";
import ListingProductInfoService from "../services/ListingProductInfoService";
import ListingAverageReviewsWidgetComponent from "./ListingAverageReviewsWidget.svelte";
import { ListingAverageReviewsWidgetV2ConfigInterface } from "./ListingAverageReviewsWidgetConfig";

@injectable()
class ListingAverageReviewsWidgetV2 {
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private component: SvelteComponent | undefined = undefined;

    constructor(
        private readonly config: ListingAverageReviewsWidgetV2ConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productIdManager: ProductIdManagerInterface
    ) {
        this.logger.debug("ListingAverageReviewsWidgetV2 config", this.config);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const listingProductInfoService = container.resolve(
            ListingProductInfoService
        );
        const listingProductInfoQuery = container.resolve(
            ListingProductInfoQuery
        );

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET_V2,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );

        this.component = new ListingAverageReviewsWidgetComponent({
            target: shadowRoot || element,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                listingProductInfoService,
                listingProductInfoQuery,
                productIdManager: this.productIdManager,
                showReviewsTooltip: this.config.showReviewsTooltip,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }

    destroy() {
        if (this.component) {
            this.component.$destroy();
        }
    }
}

export default ListingAverageReviewsWidgetV2;
